import type { FORM_REGION, FormField, UtforOppgave } from '../interfaces';
import {
    leverandorApiName,
    okonomiApiName,
    companyApiName,
    processApiName,
} from '../config';
import { generateApiRoute } from '@klp/core/api/helpers';
import { baseApi } from '@klp/core/api';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import { byggApiName } from '@klp/contextSelector/config';
import type { ContractToRegistrationProcessResponse } from '@klp/contractregistration/classes/ContractToRegistrationProcessResponse';
import {
    contractToRegistrationApiName,
    contractToRegistrationProcessApiName,
} from '@klp/contractregistration/config';
import { contractToRegistrationApi } from '@klp/contractregistration/services/rtkApi';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { baseQuery } from '@klp/core/api/services/rtkApi';
import { prosjektApi } from '@klp/prosjekt/services/rtkApi';
import type { GodkjenningProsessResponse } from '@klp/prosjekt/interfaces';
import { brukerApiName } from '@klp/user/config';

export enum ConsultantRoles {
    DRIFTS_FORVALTER = 'isDriftsForvalter',
    FORVALTER = 'isForvalter',
    HANDEL_FORVALTER = 'isHandelForvalter',
    KONTOR_FORVALTER = 'isKontorForvalter',
    OKONOMI = 'isOkonomi',
    QA_OKONOMI = 'isQAOkonomi',
}

export type Company = CompanyBrreg | CompanyDto;
export type CompanyKeys = keyof CompanyBrreg | keyof CompanyDto;

export type CompanyDto = {
    organisasjonsnummer: string;
    navn: string;
    konkurs: boolean | null;
};

export type CompanyBrreg = {
    organisasjonsnummer: string;
    navn: string;
    organisasjonsform: {
        kode: string;
        beskrivelse: string;
        _links: {
            self: {
                href: string;
            };
        };
    };
    postadresse: {
        land: string;
        landkode: string;
        postnummer: string;
        poststed: string;
        adresse: Array<string>;
        kommune: string;
        kommunenummer: string;
    };
    registreringsdatoEnhetsregisteret: string;
    registreringsdatoAntallAnsatteNAVAaregisteret: string;
    registrertIMvaregisteret: boolean;
    naeringskode1: {
        kode: string;
        beskrivelse: string;
    };
    antallAnsatte: number;
    forretningsadresse: {
        land: string;
        landkode: string;
        postnummer: string;
        poststed: string;
        adresse: Array<string>;
        kommune: string;
        kommunenummer: string;
    };
    stiftelsesdato: string;
    registrertIForetaksregisteret: boolean;
    registrertIStiftelsesregisteret: boolean;
    registrertIFrivillighetsregisteret: boolean;
    konkurs: boolean;
    underAvvikling: boolean;
    underTvangsavviklingEllerTvangsopplosning: boolean;
    maalform: string;
    _links: {
        self: {
            href: string;
        };
    };
};

enum KlassifiseringBeskrivelse {
    Godkjent = 'Godkjent',
    AlltidGodkjent = 'Alltid godkjent',
    BorIkkeBenyttes = 'Bør ikke benyttes',
    Engangsleverandor = 'Engangsleverandør',
    IkkeGodkjent = 'Ikke godkjent',
    IkkeVurdert = 'Ikke vurdert',
    KonsernIntern = 'Konsernintern',
    Utgått = 'Utgått',
}

export type Leverandorvurdering = {
    orgnummer: string;
    superOfficeContactId: number;
    leverandorId?: string;
    navn?: string;
    klassifiseringBeskrivelse?: KlassifiseringBeskrivelse;
    klassifiseringKode?: string;
};

export type ProjectBudgetInfo = {
    budsjettnummer: string;
    beskrivelse?: string;
    year: number;
    from: string;
    to: string;
    budgetExMva: number;
    mva: number;
    budgetTotal: number;
};

export type BrregCompanyResponse = {
    _embedded: {
        enheter: Array<CompanyBrreg>;
    };
    _links: {
        first: {
            href: string;
        };
        self: {
            href: string;
        };
        next: {
            href: string;
        };
        last: {
            href: string;
        };
    };
    page: {
        size: number;
        totalElements: number;
        totalPages: number;
        number: number;
    };
};

export enum Activities {
    VURDER_RELEVANS = 'Activity_1vr6y7a',
    TILDEL_OKONOMI = 'Activity_1rdcqtp',
    UTFOR_REGISTRERING_D365 = 'Activity_0hw9rg3',
    KONTROLLER_D365_REGISTRERING = 'Activity_1bk3epl',
    GODKJENNING_AV_KONTRAKT = 'Activity_1b84rij',
    TILKNYTT_DOKUMENT_TIL_KONTRAKT = 'Activity_15vq4i6',
    GJENNOMGANGSMOTE = 'Activity_1m3ak5n',
}

type SaveProsjektProcessFormfieldsRequest = Pick<
    UtforOppgave,
    'oppgaveId' | 'formFields'
>;

export type VedleggRequest = {
    name: string;
    type?: string;
    content?: string;
    contentStream?: {
        capacity: number;
        position: number;
        readTimeout: number;
        writeTimeout: number;
    };
};

export type VedleggResponse = {
    id: number;
    prosessInstansId: string;
    name: string;
    type: string | null;
    superOfficeDocumentId: string | null;
    vedleggType: string | null;
};

export type DokumentDto = {
    id: string;
    navn: string;
    filnavn: string;
};

export type BransjeRisiko = {
    id: number;
    navn: string;
    beskrivelse: string;
    risiko: number | null;
};

const executeTask = 'utforOppgave';
const role = 'rolle';
const variableConstants = 'variableConstants';
const updateForm = 'updateform';
const leverandorvurdering = 'vurdering';
const search = 'search';
const vedlegg = 'vedlegg';
const budget = 'budget';
const budgetFull = 'budgetfull';
const budgetInfo = 'info';
const multiple = 'multiple';
const nextCandidateGroup = 'nextCandidateGroup';
const dokument = 'dokument';
const garanti = 'garanti';
const kodeverk = 'kodeverk';
const bransje = 'bransje';
const risiko = 'risiko';
const klassifisering = 'klassifisering';

export type DynamicFormData = {
    id: string;
    value?: string;
};

const transformApiResponse = (response: any): Array<DynamicFormData> => {
    // if response is an array of objects, transform it here
    const result: Array<DynamicFormData> = [];

    if (Array.isArray(response)) {
        response.map((item) => {
            const formattedItem = {
                id: (item?.id ?? item?.brukerId ?? '') as string,
                value: item?.name ?? item?.navn ?? item?.fulltnavn,
            };

            result.push(formattedItem);
        });
    } else {
        // if response is an object transform it here and map the name and id to a key and value
        const formattedResponse = {
            id: (response?.id ?? response?.brukerId ?? '') as string,
            value: response?.name ?? response?.navn ?? response?.fulltnavn,
        };

        result.push(formattedResponse);
    }

    return result;
};

export const formGeneratorApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getBrregCompany: build.query<
            BrregCompanyResponse,
            { navn?: string; organisasjonsnummer?: string }
        >({
            query: (params) => {
                const queryString = `https://data.brreg.no/enhetsregisteret/api/enheter?konkurs=false&navnMetodeForSoek=FORTLOEPENDE`;

                return {
                    headers: { 'Content-Type': 'application/json' },
                    url: queryString,
                    params,
                };
            },
        }),
        getBrregCompanyByOrgnr: build.query<
            CompanyBrreg,
            { organisasjonsnummer: string }
        >({
            query: ({ organisasjonsnummer }) => ({
                url: `https://data.brreg.no/enhetsregisteret/api/enheter/${organisasjonsnummer}`,
            }),
        }),
        searchForCompany: build.query<
            Array<CompanyDto>,
            { searchValue: string; region: string }
        >({
            query: (params) => ({
                url: generateApiRoute([companyApiName, search]),
                params,
            }),
        }),
        getLeverandorvurdering: build.query<
            Leverandorvurdering,
            { orgnummer: string }
        >({
            query: (params) => ({
                url: generateApiRoute([leverandorApiName, leverandorvurdering]),
                params,
            }),
            providesTags: ['Leverandorvurdering'],
        }),
        getBransjer: build.query<Record<string, string>, string>({
            query: (selectedLanguage) => ({
                url: generateApiRoute([leverandorApiName, kodeverk, bransje]),
                params: { locale: selectedLanguage },
            }),
        }),
        getBransjerRisiko: build.query<Record<string, BransjeRisiko>, string>({
            query: (selectedLanguage) => ({
                url: generateApiRoute([
                    leverandorApiName,
                    kodeverk,
                    bransje,
                    risiko,
                ]),
                params: { locale: selectedLanguage },
            }),
            providesTags: ['BransjeRisiko'],
        }),
        getKlassifiseringer: build.query<Record<string, string>, string>({
            query: (selectedLanguage) => ({
                url: `${generateApiRoute([
                    leverandorApiName,
                    kodeverk,
                    klassifisering,
                ])}`,
                params: { locale: selectedLanguage },
            }),
        }),
        getProjectBudgets: build.query<
            Array<string>,
            { firmaId: string; fromDate?: string }
        >({
            query: ({ firmaId, fromDate }) => ({
                url: generateApiRoute([okonomiApiName, budget]),
                params: { firmaId, fromDate },
            }),
        }),
        getProjectBudgetDtos: build.query<
            Array<ProjectBudgetInfo>,
            { firmaId: string; fromDate?: string }
        >({
            query: ({ firmaId, fromDate }) => ({
                url: generateApiRoute([okonomiApiName, budgetFull]),
                params: { firmaId, fromDate },
            }),
        }),
        getProjectBudgetsInfo: build.query<
            Array<ProjectBudgetInfo>,
            { budsjettnummer: string }
        >({
            query: ({ budsjettnummer }) => ({
                url: generateApiRoute([
                    okonomiApiName,
                    budget,
                    budgetInfo,
                    multiple,
                ]),
                params: { budsjettnummer },
            }),
        }),
        getProjectBudgetInfo: build.query<
            ProjectBudgetInfo | null,
            { budsjettnummer: string }
        >({
            async queryFn(
                { budsjettnummer },
                api,
                extraOptions,
                _baseQuery,
            ): Promise<
                | { data: ProjectBudgetInfo }
                | { data: null }
                | { error: FetchBaseQueryError }
            > {
                const result = await baseQuery(
                    {
                        url: generateApiRoute([
                            okonomiApiName,
                            budget,
                            budgetInfo,
                        ]),
                        params: { budsjettnummer },
                    },
                    api,
                    extraOptions || {},
                );

                if (result.error) {
                    const code =
                        typeof result.error.status === 'number'
                            ? result.error.status
                            : result.error.status === 'PARSING_ERROR'
                              ? result.error.originalStatus
                              : 0;

                    // Catch expected error and return data the view can handle
                    if (code === 400) {
                        return { data: null };
                    }

                    return { error: result.error };
                }
                return { data: result.data as ProjectBudgetInfo };
            },
        }),
        getDynamicFormData: build.query<
            Array<DynamicFormData>,
            {
                endpoint: string;
                params?: { [param: string]: string | undefined };
            }
        >({
            query: ({ endpoint, params }) => ({
                url: generateApiRoute([endpoint], ''),
                params,
            }),
            transformResponse: (response) => transformApiResponse(response),
        }),
        getProsjektProcessRoles: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([processApiName, role]),
            }),
        }),
        getProsjektProcessVariableConstants: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([processApiName, variableConstants]),
            }),
        }),
        getProsjektByggList: build.query<Array<Bygg>, void>({
            query: () => ({
                url: generateApiRoute([byggApiName]),
                params: { includeAll: true },
            }),
        }),
        getProsjektProcessAttachments: build.query<
            Array<VedleggResponse>,
            { prosessInstansId: string; vedleggType?: string }
        >({
            query: ({ prosessInstansId, vedleggType }) => ({
                url: generateApiRoute([
                    processApiName,
                    prosessInstansId,
                    vedlegg,
                ]),
                params: { vedleggType },
            }),
            providesTags: ['VedleggList'],
        }),
        getProsjektProcessAttachmentContent: build.query<
            string,
            { attachmentId: string }
        >({
            query: ({ attachmentId }) => ({
                url: generateApiRoute([processApiName, vedlegg, attachmentId]),
            }),
            providesTags: (_result, _error, { attachmentId }) => [
                { type: 'VedleggContent', id: attachmentId },
            ],
        }),

        saveProsjektProcessAttachment: build.mutation<
            void,
            {
                prosessInstansId: string;
                vedleggType?: string;
                attachment: VedleggRequest;
            }
        >({
            query: ({ prosessInstansId, vedleggType, attachment }) => ({
                url: generateApiRoute([
                    processApiName,
                    prosessInstansId,
                    vedlegg,
                ]),
                method: 'POST',
                params: { vedleggType },
                body: attachment,
            }),
            invalidatesTags: ['VedleggList'],
        }),
        deleteProsjektProcessAttachment: build.mutation<
            void,
            { attachmentId: string }
        >({
            query: ({ attachmentId }) => ({
                url: generateApiRoute([processApiName, vedlegg, attachmentId]),
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { attachmentId }) => [
                'VedleggList',
                { type: 'VedleggContent', id: attachmentId },
            ],
        }),
        saveProsjektProcessFormfields: build.mutation<
            void,
            SaveProsjektProcessFormfieldsRequest
        >({
            query: (body) => ({
                url: generateApiRoute([processApiName, updateForm]),
                method: 'PATCH',
                body,
            }),
        }),
        saveKontraktsforvaltningProcessFormfields: build.mutation<
            void,
            SaveProsjektProcessFormfieldsRequest
        >({
            query: (body) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    contractToRegistrationProcessApiName,
                    updateForm,
                ]),
                method: 'PATCH',
                body,
            }),
        }),
        getPersonsWithRole: build.query<
            Array<DynamicFormData>,
            { rolle: string; region?: FORM_REGION }
        >({
            query: ({ rolle, region }) => ({
                url: generateApiRoute([brukerApiName]),
                method: 'GET',
                params: { rolle, region },
            }),
            transformResponse: (response) => transformApiResponse(response),
        }),
        getNextAssigneeForTask: build.query<string, { activityId: string }>({
            query: ({ activityId }) => ({
                url: generateApiRoute([processApiName, nextCandidateGroup]),
                method: 'GET',
                headers: { 'Content-Type': 'text/plain' },
                params: { oppgaveId: activityId },
                responseHandler: (response) => response.text(),
            }),
        }),
        executeProsjektTask: build.mutation<
            GodkjenningProsessResponse,
            UtforOppgave
        >({
            query: (request) => ({
                url: generateApiRoute([processApiName, executeTask]),
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['ProsjektProsess'],
        }),
        executeKostnadsforvaltningTask: build.mutation<
            ContractToRegistrationProcessResponse,
            UtforOppgave
        >({
            query: (request) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    contractToRegistrationProcessApiName,
                    executeTask,
                ]),
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Process', 'ContractToRegistrationStatistics'],
        }),
        getDokumenter: build.query<Array<DokumentDto>, void>({
            query: () => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    dokument,
                ]),
                method: 'GET',
            }),
        }),
        saveGaranti: build.mutation<
            void,
            { prosessInstansId: string; garantiFile: VedleggRequest }
        >({
            query: ({ prosessInstansId, garantiFile }) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    dokument,
                    garanti,
                    prosessInstansId,
                ]),
                method: 'POST',
                body: garantiFile,
            }),
            invalidatesTags: ['Vedlegg'],
        }),
    }),
});
const updateKontraktsforvaltningProcessWithFormfields = (
    process: ContractToRegistrationProcessResponse,
    formFields: Array<FormField>,
    activityId?: string,
) => {
    const updatedAktiviteter = process.aktiviteter?.map((a) => {
        return a.id && a.id === activityId
            ? {
                  ...a,
                  formFields: formFields,
              }
            : a;
    });

    return {
        ...process,
        aktiviteter: updatedAktiviteter,
        currentAktivitet: {
            ...process.currentAktivitet,
            formFields: formFields,
        },
    } as ContractToRegistrationProcessResponse;
};
export const updateKontraktsforvaltningProcessFormfieldsCache = (
    kontraktId: string,
    culture: string,
    formfields: Array<FormField>,
    activityId?: string,
    connected?: boolean,
) =>
    contractToRegistrationApi.util.updateQueryData(
        'getContractRegistrationProcess',
        {
            kontraktDokumentId: kontraktId,
            startNewProcess: false,
            culture: culture,
            signalRConnected: connected,
        },
        (process) =>
            updateKontraktsforvaltningProcessWithFormfields(
                process,
                formfields,
                activityId,
            ),
    );

const updateProcessWithFormfields = (
    process: GodkjenningProsessResponse,
    formFields: Array<FormField>,
    activityId?: string,
) => {
    const updatedAktiviteter = process.aktiviteter?.map((a) => {
        return a.id && a.id === activityId
            ? {
                  ...a,
                  formFields: formFields,
              }
            : a;
    });

    return {
        ...process,
        aktiviteter: updatedAktiviteter,
        currentAktivitet: {
            ...process.currentAktivitet,
            formFields: formFields,
        },
    } as GodkjenningProsessResponse;
};

export const updateProsjektProcessFormfieldsCache = (
    prosessInstansId: string,
    culture: string,
    formfields: Array<FormField>,
    activityId?: string,
) =>
    prosjektApi.util.updateQueryData(
        'getProsjektProcessByProcessId',
        { prosessInstansId: prosessInstansId, culture: culture },
        (prosjektProcess) =>
            updateProcessWithFormfields(
                prosjektProcess,
                formfields,
                activityId,
            ),
    );

export const {
    useGetProsjektByggListQuery,
    useGetBrregCompanyByOrgnrQuery,
    useLazyGetLeverandorvurderingQuery,
    useLazyGetBrregCompanyByOrgnrQuery,
    useLazySearchForCompanyQuery,
    useGetDynamicFormDataQuery,
    useExecuteProsjektTaskMutation,
    useExecuteKostnadsforvaltningTaskMutation,
    useLazyGetBrregCompanyQuery,
    useGetProsjektProcessRolesQuery,
    useGetProsjektProcessVariableConstantsQuery,
    useSaveProsjektProcessFormfieldsMutation,
    useSaveKontraktsforvaltningProcessFormfieldsMutation,
    useGetProsjektProcessAttachmentsQuery,
    useSaveProsjektProcessAttachmentMutation,
    useDeleteProsjektProcessAttachmentMutation,
    useLazyGetProsjektProcessAttachmentContentQuery,
    useLazyGetProjectBudgetsQuery,
    useLazyGetProjectBudgetDtosQuery,
    useLazyGetProjectBudgetInfoQuery,
    useLazyGetProjectBudgetsInfoQuery,
    useLazyGetNextAssigneeForTaskQuery,
    useGetPersonsWithRoleQuery,
    useGetDokumenterQuery,
    useSaveGarantiMutation,
    useGetBransjerQuery,
    useGetBransjerRisikoQuery,
    useGetKlassifiseringerQuery,
} = formGeneratorApi;
